import { useRef, useState } from 'react'
import { Camera, ChevronLeft, Trash2 } from 'lucide-react'
import { Field } from '@components/Field.tsx'
import { Header } from '@components/Header.tsx'
import { api, ApiError } from '@api'
import { useRouter } from '@router'
import {
	type ImageUploadBody,
	type Recipe,
	type RecipeCreateBody,
	RecipeTitle,
} from '@hewey/shared'
import { downscaleImage } from '@utils/downscaleImage.ts'
import {
	type DraftSection,
	emptyDraft,
	fromDrafts,
	SectionListEditor,
} from './SectionListEditor.tsx'
import {
	emptyTimingDraft,
	fromTimingDraft,
	type TimingDraft,
	TimingFields,
} from './TimingFields.tsx'
import styles from './RecipeForm.module.css'

// Field caps mirror the shared schema (RecipeIngredient 500, RecipeInstruction
// 2000); the server re-validates on save.
const INGREDIENT_MAX = 500
const INSTRUCTION_MAX = 2000

type Props = {
	// Prepend the freshly-created recipe into the shared list cache.
	onCreated: (recipe: Recipe) => void
}

export const RecipeForm = ({ onCreated }: Props) => {
	const { navigate } = useRouter()
	const [title, setTitle] = useState('')
	const [ingredients, setIngredients] = useState<DraftSection[]>(emptyDraft())
	const [instructions, setInstructions] =
		useState<DraftSection[]>(emptyDraft())
	const [timings, setTimings] = useState<TimingDraft>(emptyTimingDraft())
	// Held locally as a data URL + upload body, then uploaded once the recipe
	// row exists (image upload needs a recipe id).
	const [photo, setPhoto] = useState<{
		body: ImageUploadBody
		url: string
	} | null>(null)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const fileRef = useRef<HTMLInputElement>(null)

	const back = () => navigate('/recipes')

	const backButton = (
		<button
			type="button"
			className={styles.back}
			onClick={back}
			aria-label="Back to recipes"
		>
			<ChevronLeft size={22} aria-hidden="true" />
		</button>
	)

	const onPickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (fileRef.current) fileRef.current.value = ''
		if (!file) return
		try {
			const body = await downscaleImage(file)
			setPhoto({
				body,
				url: `data:${body.contentType};base64,${body.data}`,
			})
		} catch {
			setError('Could not read that photo.')
		}
	}

	const submit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (submitting) return
		setError(null)

		const parsedTitle = RecipeTitle.safeParse(title.trim())
		if (!parsedTitle.success) {
			setError('Give the recipe a title.')
			return
		}
		const cleanIngredients = fromDrafts(ingredients)
		if (cleanIngredients.length === 0) {
			setError('Add at least one ingredient.')
			return
		}
		const cleanInstructions = fromDrafts(instructions)

		const body: RecipeCreateBody = {
			kind: 'manual',
			title: parsedTitle.data,
			ingredients: cleanIngredients,
			instructions: cleanInstructions,
			...fromTimingDraft(timings),
		}

		setSubmitting(true)
		try {
			const { recipe } = await api.recipe.create(body)
			onCreated(recipe)
			// Best-effort photo — the recipe is saved regardless; a failed
			// upload can be retried from the detail page.
			if (photo) {
				try {
					const res = await api.recipe.uploadImage(
						recipe.id,
						photo.body,
					)
					onCreated(res.recipe)
				} catch {
					/* leave imageUrl null */
				}
			}
			navigate(`/recipes/${recipe.id}`)
		} catch (err) {
			setSubmitting(false)
			setError(
				err instanceof ApiError
					? err.message
					: 'Something went wrong saving that recipe.',
			)
		}
	}

	return (
		<main className={styles.screen}>
			<Header left={backButton} center={<h1>New recipe</h1>} />

			<form className={styles.form} onSubmit={(e) => void submit(e)}>
				<div className={styles.photo}>
					{photo ? (
						<img
							className={styles.photoImg}
							src={photo.url}
							alt="Recipe"
						/>
					) : (
						<div className={styles.photoEmpty} aria-hidden="true">
							<Camera size={28} />
						</div>
					)}
					<div className={styles.photoActions}>
						<input
							ref={fileRef}
							type="file"
							accept="image/*"
							className={styles.file}
							onChange={(e) => void onPickPhoto(e)}
						/>
						<button
							type="button"
							className={styles.photoBtn}
							onClick={() => fileRef.current?.click()}
						>
							<Camera size={15} aria-hidden="true" />
							{photo ? 'Replace' : 'Add photo'}
						</button>
						{photo ? (
							<button
								type="button"
								className={`${styles.photoBtn} ${styles.photoRemove}`}
								onClick={() => setPhoto(null)}
								aria-label="Remove photo"
							>
								<Trash2 size={15} aria-hidden="true" />
							</button>
						) : null}
					</div>
				</div>

				<Field label="Title" variant="caption">
					<input
						value={title}
						placeholder="e.g. Grandma's chicken curry"
						maxLength={200}
						onChange={(e) => setTitle(e.target.value)}
					/>
				</Field>

				<div className={styles.field}>
					<span className={styles.label}>Ingredients</span>
					<SectionListEditor
						itemNoun="ingredient"
						value={ingredients}
						onChange={setIngredients}
						itemPlaceholder="e.g. 2 tbsp olive oil"
						maxItemLength={INGREDIENT_MAX}
					/>
				</div>

				<div className={styles.field}>
					<span className={styles.label}>Instructions</span>
					<SectionListEditor
						itemNoun="step"
						value={instructions}
						onChange={setInstructions}
						itemPlaceholder="Describe a step…"
						ordered
						multiline
						maxItemLength={INSTRUCTION_MAX}
					/>
				</div>

				<TimingFields value={timings} onChange={setTimings} />

				{error ? <p className={styles.error}>{error}</p> : null}

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.cancel}
						onClick={back}
					>
						Cancel
					</button>
					<button
						type="submit"
						className={styles.submit}
						disabled={submitting}
					>
						{submitting ? 'Saving…' : 'Save recipe'}
					</button>
				</div>
			</form>
		</main>
	)
}
